import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import {
  Loader2,
  AlertCircle,
  Edit,
  Info,
  StickyNote,
  Thermometer,
  Factory,
  ArrowLeft,
  X,
} from "lucide-react";
import {
  useManufactureOrderDetailQuery,
  useUpdateManufactureOrder,
  useUpdateManufactureOrderStatus,
  useConfirmSemiProductManufacture,
  useConfirmProductCompletion,
  useDuplicateManufactureOrder,
  useOpenManufactureProtocol,
} from "../../../api/hooks/useManufactureOrders";
import { useQueryClient } from "@tanstack/react-query";
import {
  UpdateManufactureOrderRequest,
  UpdateManufactureOrderStatusRequest,
  UpdateManufactureOrderProductRequest,
  UpdateManufactureOrderSemiProductRequest,
  ConfirmSemiProductManufactureRequest,
  ConfirmProductCompletionRequest,
  ProductActualQuantityRequest,
  ResidueDistributionDto,
  ManufactureOrderState,
  ManufactureType,
} from "../../../api/generated/api-client";

// Import our extracted components
import StateTransitionControls from "../detail/StateTransitionControls";
import { deriveStateTransitions, isStateTransitionBackward } from "../detail/stateTransitions";
import BasicInfoSection from "../detail/BasicInfoSection";
import SemiProductSection from "../detail/SemiProductSection";
import ProductsDataGrid from "../detail/ProductsDataGrid";
import NotesTabContent from "../detail/NotesTabContent";
import DetailActionButtons from "../detail/DetailActionButtons";
import ConfirmationDialogs from "../detail/ConfirmationDialogs";
import ConditionsReadingsSection from "../detail/ConditionsReadingsSection";
import LotLabelPrintModal from "../../pages/LotLabelPrintModal";
import { useTelemetry } from '../../../telemetry/useTelemetry';
import { useScreenView } from '../../../telemetry/useScreenView';

interface ManufactureOrderDetailProps {
  orderId?: number;
  isOpen?: boolean;
  onClose?: () => void;
  onEdit?: (orderId: number) => void;
}

const ManufactureOrderDetail: React.FC<ManufactureOrderDetailProps> = ({
  orderId: propOrderId,
  isOpen = true,
  onClose,
  onEdit,
}) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { trackEvent } = useTelemetry();
  
  // Determine if this is modal mode or page mode
  const isModalMode = propOrderId !== undefined;
  const urlOrderId = id ? parseInt(id, 10) : 0;
  const orderId = isModalMode ? propOrderId : urlOrderId;
  
  // Tab state
  const [activeTab, setActiveTab] = useState<"info" | "notes" | "log" | "conditions">("info");

  const tabSubScreen =
    activeTab === 'info' ? 'InfoTab' :
    activeTab === 'notes' ? 'NotesTab' :
    activeTab === 'log' ? 'LogTab' :
    'ConditionsTab';
  useScreenView('Manufacturing', 'ManufactureOrderDetail', tabSubScreen);

  // Note input state
  const [newNote, setNewNote] = useState("");

  // Editable fields state
  const [editableResponsiblePerson, setEditableResponsiblePerson] = useState("");
  const [editablePlannedDate, setEditablePlannedDate] = useState("");
  const [editableSemiProductQuantity, setEditableSemiProductQuantity] = useState("");
  const [editableProductQuantities, setEditableProductQuantities] = useState<Record<number, string>>({});
  const [editableLotNumber, setEditableLotNumber] = useState("");
  const [editableExpirationDate, setEditableExpirationDate] = useState("");
  const [editableErpDiscardResidueDocumentNumber, setEditableErpDiscardResidueDocumentNumber] = useState("");
  const [editableManualActionRequired, setEditableManualActionRequired] = useState(false);

  // Track initial semi-product date from server to detect user changes
  const [initialSemiProductDate, setInitialSemiProductDate] = useState<string>("");
  
  // Confirmation dialog state
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [showStateReturnConfirmation, setShowStateReturnConfirmation] = useState(false);
  const [pendingStateChange, setPendingStateChange] = useState<ManufactureOrderState | null>(null);
  const [showQuantityConfirmModal, setShowQuantityConfirmModal] = useState(false);
  const [showProductCompletionModal, setShowProductCompletionModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showExpandedNote, setShowExpandedNote] = useState(false);
  const [showLotLabelModal, setShowLotLabelModal] = useState(false);
  const [expandedNoteContent, setExpandedNoteContent] = useState("");
  const [distributionPreview, setDistributionPreview] = useState<ResidueDistributionDto | undefined>(undefined);
  const [pendingCompletionRequest, setPendingCompletionRequest] = useState<ConfirmProductCompletionRequest | undefined>(undefined);

  // Fetch order details
  const {
    data: orderData,
    isLoading: orderLoading,
    error: orderError,
  } = useManufactureOrderDetailQuery(orderId || 0);

  const order = orderData?.order;

  // Protocol PDF hook
  const { openProtocol, isLoading: isProtocolLoading } = useOpenManufactureProtocol();

  // Mutations
  const updateOrderMutation = useUpdateManufactureOrder();
  const updateOrderStatusMutation = useUpdateManufactureOrderStatus();
  const confirmSemiProductMutation = useConfirmSemiProductManufacture();
  const confirmProductCompletionMutation = useConfirmProductCompletion();
  const duplicateOrderMutation = useDuplicateManufactureOrder();

  // Helper functions
  const getStateLabel = (state: ManufactureOrderState): string => {
    const stateKey = state.toString();
    const translationKey = `manufacture.states.${stateKey}`;
    const translated = t(translationKey);
    return translated || stateKey;
  };

  const getOrderPlannedDate = (order: any): Date => {
    if (order.plannedDate) {
      return order.plannedDate instanceof Date 
        ? order.plannedDate 
        : new Date(order.plannedDate);
    }
    return new Date();
  };


  const shouldTruncateText = (text: string): boolean => text.length > 100;
  const truncateText = (text: string): string => text.length <= 100 ? text : text.substring(0, 97) + '...';

  const formatDateTime = (date: Date | string | undefined) => {
    if (!date) return "-";
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return dateObj.toLocaleString("cs-CZ");
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return "-";
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return dateObj.toLocaleDateString("cs-CZ");
  };

  // Handler functions
  const handleClose = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["manufacture-orders"] });
    queryClient.invalidateQueries({ queryKey: ["manufactureOrders", "calendar"] });

    if (onClose) {
      onClose();
    } else {
      navigate("/manufacturing/orders");
    }
  }, [onClose, navigate, queryClient]);

  const handleCloseWithWeekNavigation = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["manufacture-orders"] });
    queryClient.invalidateQueries({ queryKey: ["manufactureOrders", "calendar"] });

    if (onClose) {
      onClose();
    } else {
      if (order) {
        const updatedPlannedDate = editablePlannedDate ? new Date(editablePlannedDate) : null;
        const orderDate = updatedPlannedDate || getOrderPlannedDate(order);
        const dateString = orderDate.toISOString().split('T')[0];
        navigate(`/manufacturing/orders?view=weekly&date=${dateString}`);
      } else {
        navigate("/manufacturing/orders");
      }
    }
  }, [onClose, navigate, queryClient, order, editablePlannedDate]);

  const handleExpandNote = (noteText: string) => {
    setExpandedNoteContent(noteText);
    setShowExpandedNote(true);
  };

  const handleStateChange = async (newState: ManufactureOrderState) => {
    if (!order || !orderId || order.state === undefined) return;

    const isBackwardTransition = isStateTransitionBackward(order.state, newState);
    const isReturnToDraft = newState === ManufactureOrderState.Draft;

    if (isBackwardTransition && !isReturnToDraft) {
      setPendingStateChange(newState);
      setShowStateReturnConfirmation(true);
      return;
    }

    await executeStateChange(newState);
  };

  const executeStateChange = async (newState: ManufactureOrderState) => {
    if (!order || !orderId) return;

    // For SinglePhase manufacturing, go directly from Planned to Completed with confirmation modal
    if (order?.manufactureType === ManufactureType.SinglePhase && order.state === ManufactureOrderState.Planned && newState === ManufactureOrderState.Completed) {
      setShowProductCompletionModal(true);
      return;
    }

    // For MultiPhase manufacturing, handle normal state transitions
    if (order.state === ManufactureOrderState.Planned && newState === ManufactureOrderState.SemiProductManufactured) {
      setShowQuantityConfirmModal(true);
      return;
    }

    if (order.state === ManufactureOrderState.SemiProductManufactured && newState === ManufactureOrderState.Completed) {
      setShowProductCompletionModal(true);
      return;
    }

    try {
      const request = new UpdateManufactureOrderStatusRequest({
        id: orderId,
        newState,
        changeReason: `Změna stavu z ${order.state !== undefined ? getStateLabel(order.state) : 'Neznámý'} na ${getStateLabel(newState)}`,
      });
      await updateOrderStatusMutation.mutateAsync(request);
    } catch (error) {
      console.error("Error updating order status:", error);
    }
  };

  // Auto-calculate lot number and expiration date
  const getWeekNumber = (date: Date): number => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };

  // Initialize editable fields when order data changes
  React.useEffect(() => {
    if (order) {
      const fieldsCanBeEdited = order.state === ManufactureOrderState.Draft || order.state === ManufactureOrderState.Planned;

      setEditableResponsiblePerson(order.responsiblePerson || "");

      const plannedDateFromServer = order.plannedDate ? new Date(order.plannedDate).toISOString().split('T')[0] : "";
      setEditablePlannedDate(plannedDateFromServer);
      setInitialSemiProductDate(plannedDateFromServer); // Track initial date from server

      const semiProductQuantity = fieldsCanBeEdited
        ? order.semiProduct?.plannedQuantity?.toString() || ""
        : order.semiProduct?.actualQuantity?.toString() || order.semiProduct?.plannedQuantity?.toString() || "";
      setEditableSemiProductQuantity(semiProductQuantity);

      setEditableLotNumber(order.semiProduct?.lotNumber || "");
      setEditableExpirationDate(order.semiProduct?.expirationDate ? new Date(order.semiProduct.expirationDate).toISOString().split('T')[0] : "");

      setEditableErpDiscardResidueDocumentNumber(order.erpDiscardResidueDocumentNumber || "");
      setEditableManualActionRequired(order.manualActionRequired || false);

      const productQuantities: Record<number, string> = {};
      order.products?.forEach((product: any, index: number) => {
        const quantity = fieldsCanBeEdited
          ? product.plannedQuantity?.toString() || ""
          : product.actualQuantity?.toString() || product.plannedQuantity?.toString() || "";
        productQuantities[index] = quantity;
      });
      setEditableProductQuantities(productQuantities);
    }
  }, [order]);

  // Auto-calculate lot number and expiration date when semi-product planned date changes
  // Only in Draft state - in Planned state, user can edit manually without auto-recalculation
  // Auto-recalculate when user actively changes the date (not on initial load)
  React.useEffect(() => {
    if (editablePlannedDate && order?.state === ManufactureOrderState.Draft) {
      // Check if the date was actually changed by the user (not just loaded from server)
      const hasUserChangedDate = editablePlannedDate !== initialSemiProductDate;

      if (hasUserChangedDate) {
        const semiProductDate = new Date(editablePlannedDate);

        // Auto-calculate lot number in wwyy format (ISO week + 2-digit year)
        const year = String(semiProductDate.getFullYear() % 100).padStart(2, '0');
        const week = String(getWeekNumber(semiProductDate)).padStart(2, '0');
        const newLotNumber = `${week}${year}`;
        setEditableLotNumber(newLotNumber);

        // Auto-calculate expiration date
        const expirationMonths = order?.semiProduct?.expirationMonths || 12;
        const expirationDate = new Date(semiProductDate);
        expirationDate.setMonth(expirationDate.getMonth() + expirationMonths + 1);
        const lastDayOfExpirationMonth = new Date(expirationDate.getFullYear(), expirationDate.getMonth() + 1, 0).getDate();
        expirationDate.setDate(lastDayOfExpirationMonth);
        const newExpirationDateString = expirationDate.toISOString().split('T')[0];
        setEditableExpirationDate(newExpirationDateString);
      }
    }
  }, [editablePlannedDate, order?.semiProduct?.expirationMonths, order?.state, initialSemiProductDate]);

  // Keyboard event listener for Esc key
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleClose]);

  // Save handler
  const handleSave = async () => {
    if (!order || !orderId) return;

    try {
      const canEditFields = order?.state === ManufactureOrderState.Draft || order?.state === ManufactureOrderState.Planned;
      
      const products = order.products?.map((product: any, index: number) => new UpdateManufactureOrderProductRequest({
        id: product.id,
        productCode: product.productCode || "",
        productName: product.productName || "",
        plannedQuantity: canEditFields ? (parseFloat(editableProductQuantities[index] || "0") || 0) : undefined,
      })) || [];

      const semiProductRequest = (editableLotNumber || editableExpirationDate || (canEditFields && editableSemiProductQuantity)) ? new UpdateManufactureOrderSemiProductRequest({
        plannedQuantity: canEditFields && editableSemiProductQuantity ? parseFloat(editableSemiProductQuantity) || undefined : undefined,
        lotNumber: editableLotNumber || undefined,
        expirationDate: editableExpirationDate ? (() => {
          const [year, month] = editableExpirationDate.split('-').map(Number);
          return new Date(year, month, 0);
        })() : undefined,
      }) : undefined;

      const request = new UpdateManufactureOrderRequest({
        id: orderId,
        plannedDate: editablePlannedDate ? new Date(editablePlannedDate) : (order.plannedDate ? new Date(order.plannedDate) : new Date()),
        responsiblePerson: editableResponsiblePerson || undefined,
        erpDiscardResidueDocumentNumber: editableErpDiscardResidueDocumentNumber || undefined,
        semiProduct: semiProductRequest,
        products,
        newNote: newNote.trim() || undefined,
        manualActionRequired: editableManualActionRequired,
      });
      
      await updateOrderMutation.mutateAsync(request);

      if (newNote.trim()) {
        setNewNote("");
      }
      
      if (onClose) {
        onClose();
      } else {
        const updatedPlannedDate = editablePlannedDate ? new Date(editablePlannedDate) : null;
        const orderDate = updatedPlannedDate || getOrderPlannedDate(order);
        const dateString = orderDate.toISOString().split('T')[0];
        navigate(`/manufacturing/orders?view=weekly&date=${dateString}`);
      }
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  const handleProductQuantityChange = (index: number, value: string) => {
    setEditableProductQuantities(prev => ({
      ...prev,
      [index]: value
    }));
  };

  const handleDuplicate = async () => {
    if (!orderId) return;

    try {
      const result = await duplicateOrderMutation.mutateAsync(orderId);

      if (result.id) {
        trackEvent('ManufactureOrderCreated', { productCode: order?.semiProduct?.productCode ?? '' });

        const newOrderUrl = `/manufacturing/orders/${result.id}`;

        if (onEdit && onClose) {
          onEdit(result.id);
        } else {
          navigate(newOrderUrl);
        }
      }
    } catch (error) {
      console.error("Error duplicating order:", error);
    }
  };

  const handleConfirmQuantity = async (request: ConfirmSemiProductManufactureRequest) => {
    try {
      await confirmSemiProductMutation.mutateAsync(request);
      setShowQuantityConfirmModal(false);
      handleCloseWithWeekNavigation();
    } catch (error) {
      console.error("Error confirming semi-product quantity:", error);
      throw error;
    }
  };

  const handleConfirmProductCompletion = async (request: ConfirmProductCompletionRequest) => {
    try {
      const response = await confirmProductCompletionMutation.mutateAsync(request);
      if (response.requiresConfirmation && response.distribution) {
        // Store the original request and show the distribution preview
        setPendingCompletionRequest(request);
        setDistributionPreview(response.distribution);
      } else {
        setShowProductCompletionModal(false);
        setDistributionPreview(undefined);
        setPendingCompletionRequest(undefined);
        handleCloseWithWeekNavigation();
      }
    } catch (error) {
      console.error("Error confirming product completion:", error);
      throw error;
    }
  };

  const handleConfirmDistribution = async () => {
    if (!pendingCompletionRequest) return;
    try {
      const overrideRequest = new ConfirmProductCompletionRequest({
        id: pendingCompletionRequest.id,
        changeReason: pendingCompletionRequest.changeReason,
        products: (pendingCompletionRequest.products ?? []).map(
          p => new ProductActualQuantityRequest({ id: p.id, actualQuantity: p.actualQuantity })
        ),
        overrideConfirmed: true,
      });
      await confirmProductCompletionMutation.mutateAsync(overrideRequest);
      setShowProductCompletionModal(false);
      setDistributionPreview(undefined);
      setPendingCompletionRequest(undefined);
      handleCloseWithWeekNavigation();
    } catch (error) {
      console.error("Error confirming distribution override:", error);
      throw error;
    }
  };

  const handleBackFromDistribution = () => {
    setDistributionPreview(undefined);
    setPendingCompletionRequest(undefined);
  };

  const handleGoToBatchCalculator = () => {
    if (!order?.semiProduct) return;

    // Get the semi-product data for navigation parameters
    const productCode = order.semiProduct.productCode;
    const plannedQuantity = order.semiProduct.plannedQuantity;

    if (productCode && plannedQuantity) {
      // Navigate to batch calculator with URL parameters
      const params = new URLSearchParams({
        productCode: productCode,
        batchSize: plannedQuantity.toString()
      });
      navigate(`/manufacturing/batch-calculator?${params.toString()}`);
    }
  };

  // Validation
  if (!isModalMode && (!id || isNaN(urlOrderId) || urlOrderId <= 0)) {
    return <Navigate to="/manufacturing/orders" replace />;
  }

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && isModalMode) {
      handleClose();
    }
  };

  const canEditFields = order?.state === ManufactureOrderState.Draft || order?.state === ManufactureOrderState.Planned;
  const currentStateTransitions = order?.state !== undefined ? deriveStateTransitions(order.state, orderData?.availableTransitions, order.manufactureType) : { next: null, previous: null };

  // Predefined label count for lot label printing: the total number of manufactured
  // product items (sum of product quantities), excluding the semi-product.
  const totalManufacturedItems = Math.round(
    Object.values(editableProductQuantities).reduce(
      (sum, quantity) => sum + (parseFloat(quantity) || 0),
      0,
    ),
  );

  const content = (
    <div className={`bg-white dark:bg-graphite-surface ${isModalMode ? 'rounded-lg shadow-xl dark:shadow-soft-dark' : ''} ${isModalMode ? 'max-w-7xl w-full max-h-[720px]' : 'h-full max-w-7xl'} overflow-hidden flex flex-col relative`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-graphite-border flex-shrink-0">
        <div className="flex items-center space-x-3">
          <Factory className="h-6 w-6 text-indigo-600 dark:text-graphite-accent" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-graphite-text">
              {order ? `Výrobní zakázka ${order.orderNumber}` : "Načítání..."}
            </h2>
            <p className="text-sm text-gray-500 dark:text-graphite-muted">ID: {orderId}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <StateTransitionControls
            order={order}
            currentStateTransitions={currentStateTransitions}
            onStateChange={handleStateChange}
            isLoading={updateOrderStatusMutation.isPending}
            getStateLabel={getStateLabel}
          />
          {onEdit && order && (
            <button
              onClick={() => onEdit(orderId)}
              className="text-gray-400 dark:text-graphite-faint hover:text-indigo-600 dark:hover:text-graphite-accent transition-colors"
              title="Upravit zakázku"
            >
              <Edit className="h-5 w-5" />
            </button>
          )}
          <button
            onClick={handleCloseWithWeekNavigation}
            className="text-gray-400 dark:text-graphite-faint hover:text-gray-600 dark:hover:text-graphite-muted transition-colors"
            title={isModalMode ? "Zavřít" : "Zpět na seznam"}
          >
            {isModalMode ? <X className="h-6 w-6" /> : <ArrowLeft className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {orderLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center space-x-2">
              <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
              <div className="text-gray-500 dark:text-graphite-muted">Načítání detailů zakázky...</div>
            </div>
          </div>
        ) : orderError ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
              <AlertCircle className="h-5 w-5" />
              <div>Chyba při načítání detailů: {orderError.message}</div>
            </div>
          </div>
        ) : order ? (
          <>
            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-graphite-border px-4 pt-3">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab("info")}
                  className={`${
                    activeTab === "info"
                      ? "border-indigo-500 text-indigo-600 dark:border-graphite-accent dark:text-graphite-accent"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-graphite-muted dark:hover:text-graphite-text dark:hover:border-graphite-border"
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <Info className="h-4 w-4 mr-2" />
                  Základní informace
                </button>
                <button
                  onClick={() => setActiveTab("notes")}
                  className={`${
                    activeTab === "notes"
                      ? "border-indigo-500 text-indigo-600 dark:border-graphite-accent dark:text-graphite-accent"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-graphite-muted dark:hover:text-graphite-text dark:hover:border-graphite-border"
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <StickyNote className="h-4 w-4 mr-2" />
                  Poznámky ({order.notes?.length || 0})
                </button>
                <button
                  onClick={() => setActiveTab("conditions")}
                  className={`${
                    activeTab === "conditions"
                      ? "border-indigo-500 text-indigo-600 dark:border-graphite-accent dark:text-graphite-accent"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-graphite-muted dark:hover:text-graphite-text dark:hover:border-graphite-border"
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <Thermometer className="h-4 w-4 mr-2" />
                  Podmínky výroby
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="overflow-y-auto p-3">
              {activeTab === "info" && (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="flex flex-col space-y-4">
                      <BasicInfoSection
                        order={order}
                        canEditFields={canEditFields}
                        editableResponsiblePerson={editableResponsiblePerson}
                        editablePlannedDate={editablePlannedDate}
                        editableLotNumber={editableLotNumber}
                        editableExpirationDate={editableExpirationDate}
                        editableManualActionRequired={editableManualActionRequired}
                        onResponsiblePersonChange={(value) => setEditableResponsiblePerson(value || "")}
                        onPlannedDateChange={setEditablePlannedDate}
                        onLotNumberChange={setEditableLotNumber}
                        onExpirationDateChange={setEditableExpirationDate}
                        onManualActionRequiredChange={setEditableManualActionRequired}
                        onResolveManualAction={() => setShowResolveModal(true)}
                        onExpandNote={handleExpandNote}
                        formatDateTime={formatDateTime}
                        formatDate={formatDate}
                        shouldTruncateText={shouldTruncateText}
                        truncateText={truncateText}
                      />
                    </div>

                    <div className="flex flex-col space-y-4">
                      {/* Hide SemiProduct section for SinglePhase manufacturing */}
                      {order?.manufactureType !== ManufactureType.SinglePhase && (
                        <SemiProductSection
                          order={order}
                          canEditFields={canEditFields}
                          editableSemiProductQuantity={editableSemiProductQuantity}
                          onSemiProductQuantityChange={setEditableSemiProductQuantity}
                        />
                      )}

                      <ProductsDataGrid
                        order={order}
                        canEditFields={canEditFields}
                        editableProductQuantities={editableProductQuantities}
                        onProductQuantityChange={handleProductQuantityChange}
                      />
                    </div>
                  </div>

                </>
              )}

              {activeTab === "notes" && (
                <NotesTabContent
                  order={order}
                  newNote={newNote}
                  onNewNoteChange={setNewNote}
                  formatDateTime={formatDateTime}
                />
              )}

              {activeTab === "conditions" && (
                <ConditionsReadingsSection readings={order?.conditionsReadings ?? []} />
              )}

            </div>
          </>
        ) : null}
      </div>

      <DetailActionButtons
        order={order}
        onCancel={() => setShowCancelConfirmation(true)}
        onDuplicate={handleDuplicate}
        onClose={handleCloseWithWeekNavigation}
        onSave={handleSave}
        onBatchCalculator={handleGoToBatchCalculator}
        onPrintProtocol={() => openProtocol(orderId)}
        onPrintLotLabels={() => setShowLotLabelModal(true)}
        isUpdateLoading={updateOrderMutation.isPending}
        isDuplicateLoading={duplicateOrderMutation.isPending}
        isPrintingProtocol={isProtocolLoading}
      />

      <LotLabelPrintModal
        isOpen={showLotLabelModal}
        onClose={() => setShowLotLabelModal(false)}
        initialLotNumber={editableLotNumber}
        initialExpirationMonth={
          editableExpirationDate ? editableExpirationDate.slice(0, 7) : ""
        }
        initialCount={totalManufacturedItems}
      />

      <ConfirmationDialogs
        showCancelConfirmation={showCancelConfirmation}
        onCancelConfirmationClose={() => setShowCancelConfirmation(false)}
        onCancelConfirm={async () => {
          setShowCancelConfirmation(false);
          await handleStateChange(ManufactureOrderState.Cancelled);
        }}
        showStateReturnConfirmation={showStateReturnConfirmation}
        pendingStateChange={pendingStateChange}
        onStateReturnConfirmationClose={() => {
          setShowStateReturnConfirmation(false);
          setPendingStateChange(null);
        }}
        onStateReturnConfirm={async () => {
          setShowStateReturnConfirmation(false);
          const stateToChange = pendingStateChange;
          setPendingStateChange(null);
          if (stateToChange) {
            await executeStateChange(stateToChange);
          }
        }}
        showQuantityConfirmModal={showQuantityConfirmModal}
        onQuantityConfirmModalClose={() => setShowQuantityConfirmModal(false)}
        onQuantityConfirm={handleConfirmQuantity}
        showProductCompletionModal={showProductCompletionModal}
        onProductCompletionModalClose={() => {
          setShowProductCompletionModal(false);
          setDistributionPreview(undefined);
          setPendingCompletionRequest(undefined);
        }}
        onProductCompletionConfirm={handleConfirmProductCompletion}
        distributionPreview={distributionPreview}
        onConfirmDistribution={handleConfirmDistribution}
        onBackFromDistribution={handleBackFromDistribution}
        showResolveModal={showResolveModal}
        onResolveModalClose={() => setShowResolveModal(false)}
        onResolveSuccess={() => {
          setShowResolveModal(false);
          queryClient.invalidateQueries({ queryKey: ["manufacture-order", orderId] });
          queryClient.invalidateQueries({ queryKey: ["manufacture-orders"] });
          queryClient.invalidateQueries({ queryKey: ["manufacture-orders", "list"] });
          queryClient.invalidateQueries({ queryKey: ["manufacture-orders", "calendar"] });
        }}
        showExpandedNote={showExpandedNote}
        expandedNoteContent={expandedNoteContent}
        onExpandedNoteClose={() => setShowExpandedNote(false)}
        order={order}
        orderId={orderId}
        isUpdateLoading={updateOrderStatusMutation.isPending}
        isQuantityLoading={confirmSemiProductMutation.isPending}
        isProductCompletionLoading={confirmProductCompletionMutation.isPending}
        getStateLabel={getStateLabel}
      />
    </div>
  );

  // Return content wrapped appropriately for modal or page mode
  if (isModalMode) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={handleBackdropClick}
      >
        {content}
      </div>
    );
  } else {
    return (
      <div className="h-screen bg-gray-50 dark:bg-graphite-surface-2 overflow-hidden">
        <div className="max-w-6xl mx-auto py-4 px-4 sm:px-6 lg:px-8 h-full">
          {content}
        </div>
      </div>
    );
  }
};

export default ManufactureOrderDetail;